import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, ArrowLeft, GenderFemale, GenderMale,
  Truck, Lightning, Rocket, CircleNotch, CheckCircle, SpotifyLogo, MicrophoneStage,
  Play, Pause,
} from "@phosphor-icons/react";
import { api, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const GENRE_KEYS = ["balada", "pop", "regional_mexicano", "rock", "reggaeton", "cumbia", "bachata", "salsa", "blues", "bolero", "rap_hiphop", "otro"];

const PLAN_PRICE = { plan_mini: 13.99, plan_pro: 17.49 };
const PLAN_ORIGINAL = { plan_mini: 19.99, plan_pro: 24.99 };
const PLAN_NAME = { plan_mini: "Plan Mini", plan_pro: "Plan Pro" };
const SPOTIFY_PRICE = 9.99;
const KARAOKE_PRICE = 9.99;
const DELIVERY_META = [
  { id: "estandar", price: 0, icon: Truck },
  { id: "rapida", price: 3.99, icon: Lightning },
  { id: "urgente", price: 5.99, icon: Rocket },
];

const variants = {
  enter: { x: 60, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -60, opacity: 0 },
};

const OptionCard = ({ active, onClick, children, testid }) => (
  <motion.button
    type="button"
    data-testid={testid}
    onClick={onClick}
    whileHover={{ scale: 1.03 }}
    whileTap={{ scale: 0.97 }}
    className={`rounded-3xl border-2 p-5 text-left font-bold transition-colors ${
      active ? "border-primary bg-primary/15 shadow-soft" : "border-border bg-white hover:border-primary/40"
    }`}
  >
    {children}
  </motion.button>
);

export default function OrderWizard({ selectedPackage }) {
  const { t } = useLang();
  const GENEROS = t("wizard.generos");
  const DELIVERY = DELIVERY_META.map((d) => ({
    ...d,
    label: t(`wizard.d_${d.id}`),
    time: t(`wizard.t_${d.id}`),
  }));
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sampleGenres, setSampleGenres] = useState([]);
  const [previewKey, setPreviewKey] = useState(null);
  const previewAudioRef = useRef(null);

  useEffect(() => {
    api.get("/genre-samples").then((r) => setSampleGenres(r.data.genres || [])).catch(() => {});
  }, []);

  const togglePreview = (key) => {
    let a = previewAudioRef.current;
    if (!a) {
      a = new Audio();
      a.addEventListener("ended", () => setPreviewKey(null));
      previewAudioRef.current = a;
    }
    if (previewKey === key) {
      a.pause();
      setPreviewKey(null);
      return;
    }
    if (a._t) clearTimeout(a._t);
    a.src = `${API}/genre-samples/${key}/audio`;
    a.currentTime = 0;
    a.play().catch(() => {});
    setPreviewKey(key);
    a._t = setTimeout(() => { a.pause(); setPreviewKey(null); }, 10000); // preview de ~10s
  };

  useEffect(() => () => { previewAudioRef.current?.pause(); }, []);

  const [form, setForm] = useState({
    genero: "",
    voz: "",
    ocasion_especial: "",
    ocasion: "",
    incluir_nombre: "",
    nombre_persona: "",
    relacion: "",
    mensaje: "",
    package_id: "",
    delivery_speed: "estandar",
    spotify: false,
    karaoke: false,
    contact_name: "",
    contact_email: "",
  });
  const [affCode, setAffCode] = useState("");
  const [affStatus, setAffStatus] = useState(null); // null | "valid" | "invalid"
  const [affKind, setAffKind] = useState("amount"); // "amount" | "free"
  const [affChecking, setAffChecking] = useState(false);

  const applyAffiliate = async () => {
    const code = affCode.trim();
    if (!code) return;
    setAffChecking(true);
    try {
      const { data } = await api.get(`/affiliates/validate/${encodeURIComponent(code)}`);
      setAffKind(data.kind || "amount");
      setAffStatus("valid");
    } catch {
      setAffKind("amount");
      setAffStatus("invalid");
    }
    setAffChecking(false);
  };

  useEffect(() => {
    if (selectedPackage) setForm((f) => ({ ...f, package_id: selectedPackage }));
  }, [selectedPackage]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const planPrice = PLAN_PRICE[form.package_id] || 0;
  const deliveryPrice = DELIVERY.find((d) => d.id === form.delivery_speed)?.price || 0;
  const spotifyPrice = form.spotify ? SPOTIFY_PRICE : 0;
  const karaokePrice = form.karaoke ? KARAOKE_PRICE : 0;
  const subtotal = planPrice + deliveryPrice + spotifyPrice + karaokePrice;
  // Un código gratis solo aplica a Plan Mini + estándar y sin extras
  const freeEligible = form.package_id === "plan_mini" && form.delivery_speed === "estandar" && !form.spotify && !form.karaoke;
  const isFreeValid = affStatus === "valid" && affKind === "free" && freeEligible;
  const isAmountValid = affStatus === "valid" && affKind === "amount";
  const affDiscount = isFreeValid ? subtotal : (isAmountValid ? 1 : 0);
  const total = Math.max(subtotal - affDiscount, 0);

  const steps = [
    {
      title: t("wizard.q_genero"),
      valid: () => !!form.genero,
      content: (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {GENEROS.map((g, idx) => {
            const key = GENRE_KEYS[idx];
            const hasSample = sampleGenres.includes(key);
            const isPreviewing = previewKey === key;
            return (
              <OptionCard key={g} testid={`genero-${g}`} active={form.genero === g} onClick={() => set("genero", g)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm">{g}</span>
                  {hasSample && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid={`genero-preview-${key}`}
                      onClick={(e) => { e.stopPropagation(); togglePreview(key); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); togglePreview(key); } }}
                      className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-bold transition-colors ${isPreviewing ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary hover:bg-primary/20"}`}
                    >
                      {isPreviewing ? <Pause weight="fill" size={11} /> : <Play weight="fill" size={11} />}
                      {t("wizard.preview")}
                    </span>
                  )}
                </div>
              </OptionCard>
            );
          })}
        </div>
      ),
    },
    {
      title: t("wizard.q_voz"),
      valid: () => !!form.voz,
      content: (
        <div className="grid grid-cols-2 gap-4">
          <OptionCard testid="voz-femenina" active={form.voz === "femenina"} onClick={() => set("voz", "femenina")}>
            <GenderFemale weight="duotone" size={40} className="text-primary mb-2" />
            <div>{t("wizard.voz_f")}</div>
          </OptionCard>
          <OptionCard testid="voz-masculina" active={form.voz === "masculina"} onClick={() => set("voz", "masculina")}>
            <GenderMale weight="duotone" size={40} className="text-secondary-foreground mb-2" />
            <div>{t("wizard.voz_m")}</div>
          </OptionCard>
        </div>
      ),
    },
    {
      title: t("wizard.q_ocasion"),
      valid: () => form.ocasion_especial === "no" || (form.ocasion_especial === "si" && form.ocasion.trim()),
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <OptionCard testid="ocasion-si" active={form.ocasion_especial === "si"} onClick={() => set("ocasion_especial", "si")}>{t("wizard.oc_si")}</OptionCard>
            <OptionCard testid="ocasion-no" active={form.ocasion_especial === "no"} onClick={() => set("ocasion_especial", "no")}>{t("wizard.oc_no")}</OptionCard>
          </div>
          {form.ocasion_especial === "si" && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
              <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.q_cual_ocasion")}</label>
              <input
                data-testid="input-ocasion"
                value={form.ocasion}
                onChange={(e) => set("ocasion", e.target.value)}
                placeholder={t("wizard.ph_ocasion")}
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
              />
            </motion.div>
          )}
        </div>
      ),
    },
    {
      title: t("wizard.q_nombre"),
      valid: () => form.incluir_nombre === "no" || (form.incluir_nombre === "si" && form.nombre_persona.trim() && form.relacion.trim()),
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <OptionCard testid="nombre-si" active={form.incluir_nombre === "si"} onClick={() => set("incluir_nombre", "si")}>{t("wizard.nombre_si")}</OptionCard>
            <OptionCard testid="nombre-no" active={form.incluir_nombre === "no"} onClick={() => set("incluir_nombre", "no")}>{t("wizard.nombre_no")}</OptionCard>
          </div>
          {form.incluir_nombre === "si" && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.l_nombre")}</label>
                <input
                  data-testid="input-nombre"
                  value={form.nombre_persona}
                  onChange={(e) => set("nombre_persona", e.target.value)}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.l_relacion")}</label>
                <input
                  data-testid="input-relacion"
                  value={form.relacion}
                  onChange={(e) => set("relacion", e.target.value)}
                  placeholder={t("wizard.ph_relacion")}
                  className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
                />
              </div>
            </motion.div>
          )}
        </div>
      ),
    },
    {
      title: t("wizard.q_mensaje"),
      subtitle: t("wizard.sub_mensaje"),
      valid: () => form.mensaje.trim().length > 10,
      content: (
        <textarea
          data-testid="input-mensaje"
          value={form.mensaje}
          onChange={(e) => set("mensaje", e.target.value)}
          rows={6}
          placeholder={t("wizard.ph_mensaje")}
          className="w-full rounded-3xl border-2 border-border bg-white px-5 py-4 font-medium focus:border-primary outline-none resize-none"
        />
      ),
    },
    {
      title: t("wizard.q_paquete"),
      valid: () => !!form.package_id,
      content: (
        <div className="grid sm:grid-cols-2 gap-3">
          {["plan_mini", "plan_pro"].map((id) => (
            <OptionCard key={id} testid={`wizard-plan-${id}`} active={form.package_id === id} onClick={() => set("package_id", id)}>
              <div className="font-display text-lg">{PLAN_NAME[id]}</div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-foreground/40 line-through">${PLAN_ORIGINAL[id]}</span>
                <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-black text-foreground">-30%</span>
              </div>
              <div className="font-display font-bold text-3xl">${PLAN_PRICE[id]}</div>
            </OptionCard>
          ))}
        </div>
      ),
    },
    {
      title: t("wizard.q_entrega"),
      valid: () => true,
      content: (
        <div className="space-y-5">
          <div className="grid gap-3">
            {DELIVERY.map((d) => {
              const Icon = d.icon;
              return (
                <OptionCard key={d.id} testid={`delivery-${d.id}`} active={form.delivery_speed === d.id} onClick={() => set("delivery_speed", d.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Icon weight="duotone" size={30} className="text-primary" />
                      <div>
                        <div>{d.label}</div>
                        <div className="text-xs text-foreground/50 font-semibold">{d.time}</div>
                      </div>
                    </div>
                    <div className="font-display font-bold text-lg">{d.price === 0 ? t("wizard.free") : `+$${d.price}`}</div>
                  </div>
                </OptionCard>
              );
            })}
          </div>
          <div>
            <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.extras")}</label>
            <div className="space-y-3">
              <OptionCard testid="addon-spotify" active={form.spotify} onClick={() => set("spotify", !form.spotify)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <SpotifyLogo weight="fill" size={32} className="text-[#1DB954]" />
                    <div>
                      <div>{t("wizard.spotify_title")}</div>
                      <div className="text-xs text-foreground/50 font-semibold">{t("wizard.spotify_desc")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-display font-bold text-lg">+${SPOTIFY_PRICE}</div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${form.spotify ? "bg-primary border-primary" : "border-border"}`}>
                      {form.spotify && <CheckCircle weight="fill" size={20} className="text-primary-foreground" />}
                    </div>
                  </div>
                </div>
              </OptionCard>
              <OptionCard testid="addon-karaoke" active={form.karaoke} onClick={() => set("karaoke", !form.karaoke)}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MicrophoneStage weight="duotone" size={32} className="text-secondary-foreground" />
                    <div>
                      <div>{t("wizard.karaoke_title")}</div>
                      <div className="text-xs text-foreground/50 font-semibold">{t("wizard.karaoke_desc")}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="font-display font-bold text-lg">+${KARAOKE_PRICE}</div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${form.karaoke ? "bg-primary border-primary" : "border-border"}`}>
                      {form.karaoke && <CheckCircle weight="fill" size={20} className="text-primary-foreground" />}
                    </div>
                  </div>
                </div>
              </OptionCard>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t("wizard.q_contacto"),
      valid: () => form.contact_name.trim() && /\S+@\S+\.\S+/.test(form.contact_email),
      content: (
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.l_tunombre")}</label>
              <input
                data-testid="input-contact-name"
                value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.l_tucorreo")}</label>
              <input
                data-testid="input-contact-email"
                type="email"
                value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                className="w-full rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium focus:border-primary outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-bold text-foreground/70 mb-2">{t("wizard.l_afiliado")}</label>
            <div className="flex gap-2">
              <input
                data-testid="input-affiliate-code"
                value={affCode}
                onChange={(e) => { setAffCode(e.target.value.toUpperCase()); setAffStatus(null); }}
                placeholder={t("wizard.ph_afiliado")}
                className="flex-1 rounded-2xl border-2 border-border bg-white px-4 py-3 font-medium uppercase focus:border-primary outline-none"
              />
              <button
                type="button"
                data-testid="apply-affiliate-btn"
                onClick={applyAffiliate}
                disabled={affChecking || !affCode.trim()}
                className="rounded-2xl bg-secondary px-5 py-3 font-bold text-secondary-foreground disabled:opacity-50 hover:bg-secondary/80 transition-colors"
              >
                {affChecking ? <CircleNotch className="animate-spin" size={18} /> : t("wizard.aff_apply")}
              </button>
            </div>
            {affStatus === "valid" && affKind === "free" && (
              freeEligible ? (
                <p data-testid="affiliate-valid-msg" className="mt-2 text-sm font-bold text-purple-600">{t("wizard.aff_free_valid")}</p>
              ) : (
                <p data-testid="affiliate-free-warning" className="mt-2 text-sm font-bold text-amber-600">{t("wizard.aff_free_restrict")}</p>
              )
            )}
            {affStatus === "valid" && affKind === "amount" && (
              <p data-testid="affiliate-valid-msg" className="mt-2 text-sm font-bold text-green-600">{t("wizard.aff_valid")}</p>
            )}
            {affStatus === "invalid" && (
              <p data-testid="affiliate-invalid-msg" className="mt-2 text-sm font-bold text-red-500">{t("wizard.aff_invalid")}</p>
            )}
          </div>
          <div className="rounded-3xl bg-muted p-5 space-y-2">
            <div className="flex items-center justify-between text-sm font-semibold text-foreground/60">
              <span>{form.package_id ? PLAN_NAME[form.package_id] : t("wizard.select_pkg")}{" · "}{DELIVERY.find((d) => d.id === form.delivery_speed)?.label}</span>
              <span>${(planPrice + deliveryPrice).toFixed(2)}</span>
            </div>
            {form.spotify && (
              <div className="flex items-center justify-between text-sm font-semibold text-foreground/60">
                <span className="flex items-center gap-1.5"><SpotifyLogo weight="fill" size={16} className="text-[#1DB954]" /> {t("wizard.spotify_title")}</span>
                <span data-testid="spotify-line-amount">+${SPOTIFY_PRICE.toFixed(2)}</span>
              </div>
            )}
            {form.karaoke && (
              <div className="flex items-center justify-between text-sm font-semibold text-foreground/60">
                <span className="flex items-center gap-1.5"><MicrophoneStage weight="duotone" size={16} className="text-secondary-foreground" /> {t("wizard.karaoke_title")}</span>
                <span data-testid="karaoke-line-amount">+${KARAOKE_PRICE.toFixed(2)}</span>
              </div>
            )}
            {isFreeValid && (
              <div className="flex items-center justify-between text-sm font-bold text-purple-600">
                <span>{t("wizard.aff_free_label")}</span>
                <span data-testid="affiliate-discount-amount">-${affDiscount.toFixed(2)}</span>
              </div>
            )}
            {isAmountValid && (
              <div className="flex items-center justify-between text-sm font-bold text-green-600">
                <span>{t("wizard.aff_discount")}</span>
                <span data-testid="affiliate-discount-amount">-$1.00</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-border/60">
              <span className="text-sm font-bold text-foreground/70">{t("wizard.total_label")}</span>
              <span data-testid="wizard-total" className="font-display font-bold text-3xl text-foreground">${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const isLast = step === steps.length - 1;

  const next = () => {
    if (!steps[step].valid()) {
      toast.error(t("wizard.incomplete"));
      return;
    }
    if (isLast) return handlePay();
    setStep((s) => s + 1);
  };

  const handlePay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        package_id: form.package_id,
        delivery_speed: form.delivery_speed,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        song_request: {
          genero: form.genero,
          voz: form.voz,
          ocasion_especial: form.ocasion_especial,
          ocasion: form.ocasion,
          incluir_nombre: form.incluir_nombre,
          nombre_persona: form.nombre_persona,
          relacion: form.relacion,
          mensaje: form.mensaje,
        },
        origin_url: window.location.origin,
        affiliate_code: affStatus === "valid" ? affCode.trim() : null,
        spotify: form.spotify,
        karaoke: form.karaoke,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || t("wizard.payError"));
      setLoading(false);
    }
  };

  const progress = ((step + 1) / steps.length) * 100;

  return (
    <section id="crear" className="relative py-24">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10">
          <p className="font-hand text-3xl text-primary mb-1">{t("wizard.kicker")}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-foreground">{t("wizard.title")}</h2>
        </div>

        <div className="rounded-[2.5rem] bg-white shadow-soft-lg border border-white p-6 sm:p-10">
          <div className="flex items-center justify-between mb-2 text-sm font-bold text-foreground/50">
            <span>{t("wizard.step")} {step + 1} {t("wizard.of")} {steps.length}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-muted mb-8 overflow-hidden">
            <motion.div className="h-full rounded-full bg-primary" animate={{ width: `${progress}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
          </div>

          <div className="min-h-[320px]">
            <AnimatePresence mode="wait">
              <motion.div key={step} variants={variants} initial="enter" animate="center" exit="exit" transition={{ type: "spring", stiffness: 260, damping: 26 }}>
                <h3 className="font-display font-semibold text-2xl sm:text-3xl text-foreground mb-1">{steps[step].title}</h3>
                {steps[step].subtitle && <p className="text-foreground/55 font-medium mb-6">{steps[step].subtitle}</p>}
                <div className={steps[step].subtitle ? "" : "mt-6"}>{steps[step].content}</div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <button
              data-testid="wizard-back"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-2 rounded-full px-5 py-3 font-bold text-foreground/60 disabled:opacity-30 hover:bg-muted transition-colors"
            >
              <ArrowLeft size={18} /> {t("wizard.back")}
            </button>
            <motion.button
              data-testid="wizard-next"
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={next}
              disabled={loading}
              className="flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-bold text-primary-foreground shadow-soft disabled:opacity-60"
            >
              {loading ? (
                <><CircleNotch className="animate-spin" size={18} /> {t("wizard.redirecting")}</>
              ) : isLast ? (
                <><CheckCircle weight="fill" size={18} /> {total === 0 ? t("wizard.getFree") : t("wizard.pay")}</>
              ) : (
                <>{t("wizard.next")} <ArrowRight size={18} /></>
              )}
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}
